/**
 * Org membership admission — DEV-1414 (SCOPING_COMMUNITY_MEMBERSHIP.md §8.7).
 *
 * The two in-scope admission doors for org communities, sharing ONE data
 * mechanism (community_memberships status ladder + workflow_requests):
 *
 *   1. Request → approval:  submitMembershipRequest()
 *      Writes a status='pending' membership row at event time (DEV-1372
 *      population-path rule) + a `community.membership_request` workflow
 *      assigned to the community's admins.
 *
 *   2. Invitation (existing platform user):  sendMembershipInvitation()
 *      Writes a status='invited' membership row + a
 *      `community.membership_invitation` workflow assigned to the invitee.
 *
 * Resolution (approve/decline/accept) happens through the standard
 * workflow engine + workflowSideEffects — the org Management → Membership
 * tab and the Action Center inbox are two views over the SAME
 * workflow_requests rows (§8.7 one-source ruling).
 *
 * Roster preload (DEV-1313) and email/new-user invitations
 * (DEV-1307/869/870 + DEV-1311 lineage) are the deferred doors.
 */

import { upsertCommunityMembership } from "../../api/security";
import { addOrUpdateWorkflow, submitRequest } from "../../workflow";
import type { WorkflowRequest } from "../../workflow";

/** RFC 4122 v4 GUID — same pattern as CommunityPermissionsV2Panel (DEV-1017:
 *  backend WorkflowGuid is System.Guid; non-GUID strings fail deserialization). */
function makeGuid(): string {
  try {
    const c: any = (globalThis as any).crypto;
    if (c?.randomUUID) return String(c.randomUUID());
  } catch {
    /* fall through */
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Community admins from the V1 role rows (owner/admin) — the assignment
 * list for request workflows. V1 read is acceptable here: the DEV-1022
 * bridge dual-writes V1 rows on every V2 admin grant, and the V1→V2 role
 * cutover is fenced to DEV-997/1000. The Membership tab queue does NOT
 * depend on this list (it filters by target_guid), so an empty result
 * only affects Action Center routing, never data.
 */
export function resolveCommunityAdminGuids(
  communityRoles: Record<string, any>[] | undefined,
  communityGuid: string,
): string[] {
  const cg = String(communityGuid || "").toUpperCase();
  const out = new Set<string>();
  for (const r of communityRoles || []) {
    const role = String(r.role || "").toLowerCase();
    if (
      String(r.community_entity_guid || "").toUpperCase() === cg &&
      (role === "admin" || role === "owner") &&
      (r.is_active ?? true) !== false
    ) {
      const g = String(r.user_guid || "").trim();
      if (g) out.add(g);
    }
  }
  return Array.from(out);
}

export interface MembershipRequestArgs {
  currentUserGuid: string;
  currentUserName: string;
  communityGuid: string;
  communityName: string;
  message: string;
  /** From resolveCommunityAdminGuids — routes the Action Center obligation. */
  adminGuids: string[];
}

/** Door 1 — member requests to join an org community. */
export async function submitMembershipRequest(args: MembershipRequestArgs): Promise<void> {
  const now = new Date();
  const trimmed = (args.message || "").trim();

  // Population-path rule (DEV-1372): the declared fact is written at event
  // time — a pending membership row, visible on the admin roster as Pending.
  await upsertCommunityMembership({
    userGuid: args.currentUserGuid,
    communityEntityGuid: args.communityGuid,
    membershipType: "org_member",
    status: "pending",
  });

  const draft: WorkflowRequest = {
    workflow_guid: makeGuid(),
    target_type: "community",
    target_guid: args.communityGuid,
    action_type: "community.membership_request",
    title: `Membership request — ${args.communityName}`,
    description: trimmed
      ? `${trimmed}\n\n— ${args.currentUserName} requested to join ${args.communityName}.`
      : `${args.currentUserName} requested to join ${args.communityName}.`,
    payload: {
      community_guid: args.communityGuid,
      community_name: args.communityName,
      user_guid: args.currentUserGuid,
      user_display_name: args.currentUserName,
      membership_type: "org_member",
      custom_message: trimmed,
    },
    status: "draft",
    version: 1,
    created_by: args.currentUserGuid,
    created_at: now.toISOString(),
    updated_at: now.toISOString(),
    assigned_to_guids: args.adminGuids,
    history: [{ action: "draft", by: args.currentUserGuid, at: now.toISOString() }],
  };
  // Single write (DEV-1017 followup — draft state is never user-visible).
  addOrUpdateWorkflow(submitRequest(draft, args.currentUserGuid));
}

export interface MembershipInvitationArgs {
  inviterGuid: string;
  inviterName: string;
  inviteeGuid: string;
  inviteeName: string;
  communityGuid: string;
  communityName: string;
  message: string;
}

/** Door 3 (in-platform flavor) — admin invites an existing user to the org. */
export async function sendMembershipInvitation(args: MembershipInvitationArgs): Promise<void> {
  const now = new Date();
  const trimmed = (args.message || "").trim();

  await upsertCommunityMembership({
    userGuid: args.inviteeGuid,
    communityEntityGuid: args.communityGuid,
    membershipType: "org_member",
    status: "invited",
  });

  const draft: WorkflowRequest = {
    workflow_guid: makeGuid(),
    target_type: "community",
    target_guid: args.communityGuid,
    action_type: "community.membership_invitation",
    title: `Membership invitation — ${args.communityName}`,
    description: trimmed
      ? `${trimmed}\n\n— You've been invited to join ${args.communityName}.`
      : `You've been invited to join ${args.communityName}.`,
    payload: {
      community_guid: args.communityGuid,
      community_name: args.communityName,
      user_guid: args.inviteeGuid,
      user_display_name: args.inviteeName,
      membership_type: "org_member",
      custom_message: trimmed,
    },
    status: "draft",
    version: 1,
    created_by: args.inviterGuid,
    created_at: now.toISOString(),
    updated_at: now.toISOString(),
    assigned_to_guids: [args.inviteeGuid],
    history: [{ action: "draft", by: args.inviterGuid, at: now.toISOString() }],
  };
  addOrUpdateWorkflow(submitRequest(draft, args.inviterGuid));
}
